import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { User, Ruler, Star, ArrowLeft, Clock, Lock } from 'lucide-react';

// Experience level display names
const experienceLevelLabels = {
  'no_experience': 'New',
  'some_extra_experience': 'Extra Background',
  'extensive_extra_experience': 'Experienced Extra',
  'some_standin_experience': 'Stand-In Experience',
  'extensive_standin_experience': 'Extensive Stand-In'
};

const SharedProfilePage = () => {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfiles();
  }, [shareToken]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareToken}`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 410) {
        setError('This share link has expired.');
      } else if (err.response?.status === 404) {
        setError('Share link not found.');
      } else {
        setError('Failed to load profiles.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h1 className="text-white text-2xl font-bold mb-2">Oops!</h1>
          <p className="text-white/60">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] mt-4 hover:underline">
            <ArrowLeft className="w-4 h-4" />
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const profiles = data?.profiles || [];
  const expiresAt = data?.expires_at ? new Date(data.expires_at) : null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-28 w-auto"
            />
          </Link>
          <div className="flex items-center gap-2 text-white/40 text-sm">
            <Lock className="w-4 h-4" />
            <span>Restricted Preview</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl font-bold text-white mb-2">
            Stand-In Candidates
          </h1>
          <p className="text-white/60">
            {profiles.length} profile{profiles.length !== 1 ? 's' : ''} for your review
          </p>
          {expiresAt && (
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#F59E0B]/10 rounded-full text-[#F59E0B] text-sm">
              <Clock className="w-4 h-4" />
              Expires: {expiresAt.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'short', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-4 mb-8 flex items-start gap-3">
          <Lock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium">Restricted Preview</p>
            <p className="text-white/60 text-sm mt-1">
              This view shows limited information. Full profiles including contact details will be shared once you confirm your selection with 2nd Team Casting.
            </p>
          </div>
        </div>

        {/* Profile Cards - Restricted View */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {profiles.map((profile) => (
            <div key={profile.id} className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors">
              {/* Photo */}
              <div className="aspect-square bg-[#0A0A0A] relative">
                {profile.headshot_url ? (
                  <img 
                    src={profile.headshot_url} 
                    alt={profile.first_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-20 h-20 text-white/10" />
                  </div>
                )}
                
                {/* Experience Badge */}
                {profile.experience_level && (
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full uppercase">
                      {experienceLevelLabels[profile.experience_level] || profile.experience_level}
                    </span>
                  </div>
                )}
              </div>

              {/* Details - RESTRICTED */}
              <div className="p-5">
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-3">
                  {profile.first_name}
                </h2>
                
                {/* Height */}
                {profile.height && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Ruler className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-lg">{profile.height}</span>
                  </div>
                )}
                
                {/* Experience Level */}
                {profile.experience_level && (
                  <div className="mt-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white/60 text-sm">
                      {experienceLevelLabels[profile.experience_level] || profile.experience_level}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Notice */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-[#121212] border border-[#D4AF37]/30 rounded-xl p-6 max-w-lg">
            <h3 className="text-[#D4AF37] font-semibold mb-2">Interested in any of these stand-ins?</h3>
            <p className="text-white/60 text-sm mb-4">
              Contact 2nd Team Casting to confirm your selection. Once confirmed, you'll receive full profile details including contact information.
            </p>
            <a 
              href="mailto:info@2ndteam.co.uk"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-bold rounded-lg hover:brightness-110 transition-all"
            >
              Contact 2nd Team
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-white/40 text-sm">
          © 2026 2nd Team Casting. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default SharedProfilePage;
